/**
 * This module updates the result counter when number of stores that are shown have changed.
 *
 * @author  Meinaart van Straalen
 * @author  Tamara Bazko
 **/

import $ from 'jquery';
import Factory from 'components/utils/Factory';
import createLogger from 'components/logger/Logger';
import EventTypes from 'components/EventTypes';
import StoreFinderComponent from './StoreFinderComponent';
import device from 'components/utils/device';
import 'jquery.extendPrototype';
const Logger = createLogger('MapSearchManager');
function StoreFinderSearch($element, settings) {
  Logger.info('Created');

  settings = settings || {};
  settings.selector = $element;
  StoreFinderComponent.call(this, settings, module);

  this.Search = this.settings.Search;

  this.$clearButton = this.findElement(this.settings.clearButtonSelector);
  this.$inputElement = this.findElement(this.settings.inputSelector);
  this.$inputContainer = this.$inputElement.parents(this.settings.inputContainerSelector);
  this.$locationButton = this.findStoreFinderElement(this.settings.locationButtonSelector);

  this.defaultCurrentLocationLabel = window.labels.storeFinder.currentLocation;
  this.bindEvents();
}

$.extendPrototype(StoreFinderSearch, StoreFinderComponent.prototype, {
  $clearButton: undefined,
  $inputContainer: undefined,
  $inputElement: undefined,
  $locationButton: undefined,
  $messageBlock: undefined,
  $messageBox: undefined,

  currentLocationLabel: undefined,
  defaultCurrentLocationLabel: '',
  userLocation: false,

  settings: {
    clearButtonSelector: '.js-storeFinder-search-clearButton',
    inputContainerSelector: '.js-formInputGroup',
    inputSelector: '.js-storeFinder-search-input',
    locationButtonSelector: '.js-storeFinder-search-locationButton',
    selector: '.js-storeFinder-search',
    Search: undefined,
  },

  /**
   * Bind events
   */
  bindEvents() {
    document.jq
      .on(EventTypes.STOREFINDER_MAP_INITIALIZED, this.onMapInitialized.bind(this))
      .on(EventTypes.STOREFINDER_GET_STORES, this.onGetStores.bind(this))
      .on(EventTypes.STOREFINDER_GET_STORES_REQUEST, this.onGetStoresRequest.bind(this))
      .on(EventTypes.STOREFINDER_GET_USER_LOCATION, this.onGetUserLocation.bind(this))
      .on(EventTypes.STOREFINDER_GET_USER_LOCATION_ERROR, this.onGetUserLocationError.bind(this))
      .on(EventTypes.STOREFINDER_SEARCH_PLACES_CHANGED, this.onPlaceSelected.bind(this))
      .on(EventTypes.STOREFINDER_STORE_DETAILS_OPEN, this.onStoreDetailOpen.bind(this));

    this.$clearButton.on('vclick', this.onClearButtonClick.bind(this));
    this.$locationButton.on('vclick', this.onSearchLocationButtonClick.bind(this));
    this.$inputElement.on('focus blur change keyup', this.onInputFieldChanged.bind(this));
  },

  /**
   * Clear input field when user clicks on clear button
   */
  onClearButtonClick() {
    this.$inputElement.val('').trigger('change');
    this.$clearButton.removeClass('is-visible');
    this.$locationButton.removeClass('is-location-available');
    this.toggleFeedbackMessage();
  },

  /**
   * When search location button has been clicked
   */
  onSearchLocationButtonClick() {
    document.jq.trigger(EventTypes.STOREFINDER_SEARCH_LOCATION_BUTTON_CLICKED);
    this.Search.triggerEnterKey.bind(this, this.$inputElement);
  },

  /**
   * Remove loading state from search button when stores are retrieved
   */
  onGetStores() {
    this.$inputContainer.removeClass('is-loading');
  },

  /**
   * Add loading state to search button when stores are being retrieved
   */
  onGetStoresRequest() {
    this.$inputContainer.addClass('is-loading');
    this.$locationButton.addClass('is-location-available');
  },

  /**
   * Event handler for when user location is retrieved
   *
   * @param  {object} event     jQuery event
   * @param  {object} eventData Event data
   */
  onGetUserLocation(event, eventData) {
    Logger.debug('onGetUserLocation', eventData);
    if (eventData && eventData.fromGPS) {
      this.userLocation = true;
      this.$inputElement.val(window.labels.storeFinder.currentLocation);
      this.$storeFinder.addClass('is-location-available');
      this.$locationButton.removeClass('is-location-available');
      this.onInputFieldChanged();
    }
  },

  /**
   * Remove loading state from location button when retrieving user location
   * generated an error (usually if permission is denied).
   */
  onGetUserLocationError() {
    this.$storeFinder.removeClass('is-location-available');
  },

  /**
   * If value of input field is equal to `this.defaultCurrentLocationLabel`
   * the searchBox element is hidden, otherwise it gets it default visibility state
   */
  onInputFieldChanged(event) {
    const eventType = event ? event.type : false;

    if (this.$inputElement) {
      if (eventType === 'focus' && this.$inputElement.val() === this.defaultCurrentLocationLabel) {
        this.$inputElement.val('');
      } else if (eventType === 'blur') {
        if (device.hasTouch) {
          window.jq.scrollTop(0);
        }
        if (this.$inputElement.val() === '' && this.userLocation) {
          this.$inputElement.val(this.currentLocationLabel || this.defaultCurrentLocationLabel);
        }
      } else {
        if (this.defaultCurrentLocationLabel) {
          this.Search.hideSuggestedSearch(
            this.$inputElement.val() === this.defaultCurrentLocationLabel
          );
        }
      }

      const hasValue = this.$inputElement.val() !== '';
      this.$clearButton.toggleClass('is-visible', hasValue);

      // only reset feedback when no Enter (13) is given
      if (eventType === 'keyup' && event.keyCode !== 13) {
        this.toggleFeedbackMessage();
      }
    }
  },

  /**
   * Event handler for when the Map is initialized
   * @param  {object} event     jQuery event
   * @param  {object} eventData Event data, contains `map` property
   */
  onMapInitialized(event, eventData) {
    Logger.debug('onMapInitialized, initializing searchBox');
    const settings = {
      map: eventData.map,
      inputElement: this.$inputElement.get(0),
      searchContainer: this.$element,
      clearButton: this.$clearButton,
      locationButton: this.$locationButton,
    };

    this.Search.createSearchBox(settings);
    this.Search.bindEvents();
  },

  /**
   * Event handler for when a place is selected via the searchBox
   */
  onPlaceSelected(event) {
    const geometry = this.Search.getPlaceGeometry();
    this.currentLocationLabel = this.$inputElement.val();

    if (typeof geometry === 'object') {
      Logger.debug('Place changed:', geometry, this.Search._place);
      this.Search.placeSelected();
    } else if (geometry === false) {
      Logger.debug("SearchBox's returned place contains no geometry");
      this.toggleFeedbackMessage(window.labels.storeFinder.noAddressFound, 'error');
    }
  },

  toggleFeedbackMessage(message, type) {
    this.$messageBlock = this.$messageBlock || $('.js-storeFinder-feedback');
    this.$messageBox = this.$messageBox || this.$messageBlock.find('.js-message');
    if (!!message && !!type) {
      this.$messageBlock.removeClass('is-hidden');
      this.$messageBox.addClass(`${type}`).html(message);
    } else {
      this.$messageBlock.addClass('is-hidden');
      this.$messageBox.removeClass().addClass('message js-message').empty();
    }
  },

  /**
   * Blur input, when going to detail page (otherwise can sometimes keep focus
   * and suggestions open on iOS)
   */
  onStoreDetailOpen() {
    this.$inputElement.blur();
  },
});

export default Factory.create(StoreFinderSearch);
